import React, { useState, useEffect } from 'react';
import { bookings, timeSlots, waitlist } from '../services/api';
import { toast } from 'react-toastify';
import { format, addDays, startOfWeek, startOfMonth, endOfMonth, isSameDay, parseISO, isSameMonth, addMonths, subMonths, getDaysInMonth, getDay } from 'date-fns';
import { pt } from 'date-fns/locale';
import { DashboardLayout } from '../components/Layout';
import './ClientDashboard.css';

export function ClientDashboard({ tab = 'booking' }) {
  // O separador vem da rota: /reservas (marcar) e /reservas/minhas (as minhas reservas). Os endereços antigos /?tab=… redirecionam (utils/legacyRoutes).
  const activeTab = tab;
  const [myBookings, setMyBookings] = useState([]);
  const [myWaitlist, setMyWaitlist] = useState([]);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [slotsError, setSlotsError] = useState(false);
  const [bookingsError, setBookingsError] = useState(false);
  const [waitlistError, setWaitlistError] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // Estado para marcação
  const [selectedDate, setSelectedDate] = useState(null);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [slotsForDate, setSlotsForDate] = useState([]);
  
  // Estado para paginação
  const [upcomingPage, setUpcomingPage] = useState(0);
  const [historyPage, setHistoryPage] = useState(0);
  const itemsPerPage = 5;

  useEffect(() => {
    loadData();
  }, []);

  // `loadSettings` saiu: as configurações do ginásio eram pedidas, guardadas em estado e nunca lidas
  // por nada deste ecrã — uma chamada a mais em cada abertura, cuja falha só aparecia na consola.

  const loadAvailableSlots = async () => {
    try {
      const startDate = startOfMonth(currentMonth);
      const endDate = endOfMonth(currentMonth);
      const response = await timeSlots.getAll({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString()
      });
      setAvailableSlots(response.data || []);
      setSlotsError(false);
    } catch (error) {
      // Antes ficava só no console: o calendário aparecia sem um único horário e o atleta lia
      // «Sem horários disponíveis», que é o mesmo que vê quando o ginásio não tem aulas marcadas.
      console.error('Erro ao carregar horários');
      setSlotsError(true);
    }
  };

  const loadWaitlist = async () => {
    try {
      const response = await waitlist.getMy();
      setMyWaitlist(response.data || []);
      setWaitlistError(false);
    } catch (error) {
      // Uma lista de espera que não carrega não pode passar por «não estou em nenhuma»: quem já está
      // na fila voltava a ver «Entrar na lista de espera», e quem foi notificado de uma vaga perdia o
      // botão de aceitar (o servidor recusava a seguir).
      console.error('Erro ao carregar lista de espera');
      setWaitlistError(true);
    }
  };

  useEffect(() => {
    if (activeTab === 'booking') {
      loadAvailableSlots();
    }
  }, [activeTab, currentMonth]);

  useEffect(() => {
    if (selectedDate && availableSlots.length > 0) {
      const slots = availableSlots.filter(slot => {
        const slotDate = parseISO(slot.startTime);
        return isSameDay(slotDate, selectedDate);
      }).sort((a, b) => new Date(a.startTime) - new Date(b.startTime));
      setSlotsForDate(slots);
    } else {
      setSlotsForDate([]);
    }
  }, [selectedDate, availableSlots]);

  const loadData = async () => {
    try {
      const bookingsRes = await bookings.getAll({});
      setMyBookings(bookingsRes.data || []);
      setBookingsError(false);
      loadWaitlist();
    } catch (error) {
      // Sem isto, uma falha mostrava «Nenhuma reserva encontrada» e o atleta concluía que as
      // reservas dele tinham desaparecido.
      console.error('Erro ao carregar dados');
      setBookingsError(true);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelBooking = async (id) => {
    if (window.confirm('Tem certeza que deseja cancelar esta marcação?')) {
      try {
        await bookings.cancel(id);
        toast.success('Marcação cancelada');
        loadData();
      } catch (error) {
        toast.error(error.response?.data?.message || 'Erro ao cancelar marcação');
      }
    }
  };

  const handleCheckIn = async (id) => {
    try {
      await bookings.checkIn(id);
      toast.success('Check-in realizado com sucesso!');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao fazer check-in');
    }
  };

  const handleBookSlot = async (slotId) => {
    try {
      await bookings.create({ timeSlotId: slotId });
      toast.success('Marcação realizada com sucesso!');
      loadData();
      loadAvailableSlots();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao fazer marcação');
    }
  };

  const handleJoinWaitlist = async (slotId) => {
    try {
      await waitlist.join(slotId);
      toast.success('Adicionado à lista de espera!');
      loadWaitlist();
      loadAvailableSlots();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao entrar na lista de espera');
    }
  };

  const handleLeaveWaitlist = async (entryId) => {
    try {
      await waitlist.leave(entryId);
      toast.success('Saiu da lista de espera');
      loadWaitlist();
      loadAvailableSlots();
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao sair da lista de espera');
    }
  };

  // Quando abre uma vaga, o servidor avisa (Notified) e a vaga passa a reserva com um clique.
  const handleAcceptWaitlist = async (entryId) => {
    try {
      await waitlist.accept(entryId);
      toast.success('Vaga aceite: a reserva está feita');
      loadWaitlist();
      loadAvailableSlots();
      loadData();          // a reserva nova entra na lista de marcações
    } catch (error) {
      toast.error(error.response?.data?.message || 'Erro ao aceitar a vaga');
    }
  };

  const waitlistEntryFor = (slotId) => myWaitlist.find(w => w.timeSlotId === slotId);

  const nextMonth = () => setCurrentMonth(addMonths(currentMonth, 1));
  const prevMonth = () => setCurrentMonth(subMonths(currentMonth, 1));

  const isSlotBooked = (slotId) => {
    return myBookings.some(booking => booking.timeSlotId === slotId && !booking.isCancelled);
  };

  const getSlotStatus = (slot) => {
    if (slot.isBlocked) return 'blocked';
    if (isSlotBooked(slot.id)) return 'booked';
    const now = new Date();
    const slotDate = parseISO(slot.startTime);
    if (slotDate < now) return 'past';
    if (slot.isFull || (slot.currentBookings >= slot.maxCapacity)) return 'full';
    return 'available';
  };

  const hasAvailableSlots = (date) => {
    return availableSlots.some(slot => {
      const slotDate = parseISO(slot.startTime);
      return isSameDay(slotDate, date) && getSlotStatus(slot) === 'available';
    });
  };

  // Gerar calendário
  const generateCalendarDays = () => {
    const year = currentMonth.getFullYear();
    const month = currentMonth.getMonth();
    const firstDayOfMonth = new Date(year, month, 1);
    const daysInMonth = getDaysInMonth(currentMonth);
    const startingDayOfWeek = getDay(firstDayOfMonth);
    const adjustedStartDay = startingDayOfWeek === 0 ? 6 : startingDayOfWeek - 1;
    
    const days = [];
    
    const prevMonthLastDay = new Date(year, month, 0).getDate();
    for (let i = adjustedStartDay - 1; i >= 0; i--) {
      days.push({ date: new Date(year, month - 1, prevMonthLastDay - i), isCurrentMonth: false });
    }
    
    for (let i = 1; i <= daysInMonth; i++) {
      days.push({ date: new Date(year, month, i), isCurrentMonth: true });
    }
    
    const remainingDays = 42 - days.length;
    for (let i = 1; i <= remainingDays; i++) {
      days.push({ date: new Date(year, month + 1, i), isCurrentMonth: false });
    }
    
    return days;
  };

  // Próximas reservas (ordenadas por data futura)
  const allUpcomingBookings = myBookings
    .filter(b => !b.isCancelled && new Date(b.slotStartTime) > new Date())
    .sort((a, b) => new Date(a.slotStartTime) - new Date(b.slotStartTime));
  
  const upcomingBookings = allUpcomingBookings.slice(0, 3);
  
  // Paginação das próximas reservas
  const totalUpcomingPages = Math.ceil(allUpcomingBookings.length / itemsPerPage);
  const paginatedUpcoming = allUpcomingBookings.slice(
    upcomingPage * itemsPerPage, 
    (upcomingPage + 1) * itemsPerPage
  );
  
  // Histórico (todas as reservas ordenadas por data mais recente)
  const allHistory = [...myBookings].sort((a, b) => 
    new Date(b.slotStartTime) - new Date(a.slotStartTime)
  );
  const totalHistoryPages = Math.ceil(allHistory.length / itemsPerPage);
  const paginatedHistory = allHistory.slice(
    historyPage * itemsPerPage, 
    (historyPage + 1) * itemsPerPage
  );

  if (loading) {
    return (
      <DashboardLayout userType="client">
        <div className="loading-container">
          <div className="spinner"></div>
          <p>A carregar...</p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout userType="client">
      {/* Content */}
      <div className="tab-content">
        {/* Booking Calendar */}
        {activeTab === 'booking' && (
          <div className="booking-section">
            {slotsError && (
              <div className="empty-state" role="alert" style={{ marginBottom: 12 }}>
                <p>Não foi possível carregar os horários — isto não quer dizer que não há aulas.</p>
                <button type="button" className="btn btn-outline" onClick={loadAvailableSlots}>Tentar de novo</button>
              </div>
            )}
            {waitlistError && (
              <div className="empty-state" role="alert" style={{ marginBottom: 12 }}>
                <p>Não foi possível confirmar o estado das suas listas de espera.</p>
                <button type="button" className="btn btn-outline" onClick={loadWaitlist}>Tentar de novo</button>
              </div>
            )}
            <div className="data-card">
              <div className="card-header">
                <h3 className="card-title">
                  {selectedDate 
                    ? `Horários - ${format(selectedDate, 'dd MMMM', { locale: pt })}`
                    : 'Selecione uma Data'
                  }
                </h3>
                {selectedDate && (
                  <button className="btn btn-outline" onClick={() => setSelectedDate(null)}>
                    ← Voltar
                  </button>
                )}
              </div>

              {!selectedDate ? (
                <div className="calendar-container">
                  <div className="calendar-nav">
                    <button onClick={prevMonth}>◀</button>
                    <span>{format(currentMonth, 'MMMM yyyy', { locale: pt })}</span>
                    <button onClick={nextMonth}>▶</button>
                  </div>
                  
                  <div className="calendar-grid">
                    <div className="calendar-weekdays">
                      {['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'].map(d => (
                        <div key={d} className="weekday">{d}</div>
                      ))}
                    </div>
                    <div className="calendar-days">
                      {generateCalendarDays().map((dayObj, i) => {
                        const isToday = isSameDay(dayObj.date, new Date());
                        const isPast = dayObj.date < new Date() && !isToday;
                        const hasSlots = hasAvailableSlots(dayObj.date);
                        
                        return (
                          <div
                            key={i}
                            className={`calendar-day 
                              ${!dayObj.isCurrentMonth ? 'other-month' : ''} 
                              ${isToday ? 'today' : ''} 
                              ${isPast ? 'past' : ''} 
                              ${hasSlots ? 'has-slots' : 'no-slots'}`}
                            onClick={() => {
                              if (!isPast && dayObj.isCurrentMonth && hasSlots) {
                                setSelectedDate(dayObj.date);
                              }
                            }}
                          >
                            {dayObj.date.getDate()}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  
                  <div className="calendar-legend">
                    <span className="legend-item"><span className="dot available"></span> Disponível</span>
                    <span className="legend-item"><span className="dot unavailable"></span> Sem vagas</span>
                  </div>
                </div>
              ) : (
                <div className="slots-container">
                  {slotsForDate.length === 0 ? (
                    <div className="empty-state">
                      {slotsError ? (
                        <>
                          <p>Não foi possível carregar os horários. Isto não quer dizer que não há aulas.</p>
                          <button type="button" className="btn-secondary" onClick={loadAvailableSlots}>Tentar de novo</button>
                        </>
                      ) : (
                        <p>Sem horários para esta data</p>
                      )}
                    </div>
                  ) : (
                    <div className="slots-grid">
                      {slotsForDate.map(slot => {
                        const status = getSlotStatus(slot);
                        const available = slot.maxCapacity - (slot.currentBookings || 0);
                        
                        return (
                          <div 
                            key={slot.id}
                            className={`slot-card ${status}`}
                            onClick={() => status === 'available' && handleBookSlot(slot.id)}
                          >
                            <div className="slot-time">{format(parseISO(slot.startTime), 'HH:mm')}</div>
                            <div className="slot-info">
                              <span>{available} vagas</span>
                            </div>
                            {status === 'booked' && <div className="slot-badge">✓ Reservado</div>}
                            {status === 'full' && (() => {
                              // A lista de espera existia na API e não tinha por onde se entrar (o ecrã nunca a mostrava).
                              const entry = waitlistEntryFor(slot.id);
                              if (!entry) {
                                return (
                                  <button
                                    type="button"
                                    className="slot-badge full slot-waitlist-btn"
                                    onClick={(e) => { e.stopPropagation(); handleJoinWaitlist(slot.id); }}
                                  >
                                    Entrar na lista de espera
                                  </button>
                                );
                              }
                              if (entry.status === 'Notified') {
                                return (
                                  <button
                                    type="button"
                                    className="slot-badge slot-waitlist-btn"
                                    onClick={(e) => { e.stopPropagation(); handleAcceptWaitlist(entry.id); }}
                                  >
                                    Vaga livre — aceitar
                                  </button>
                                );
                              }
                              return (
                                <div className="slot-badge full">
                                  Na lista de espera · n.º {entry.position}
                                  <button
                                    type="button"
                                    className="slot-waitlist-leave"
                                    title="Sair da lista de espera"
                                    onClick={(e) => { e.stopPropagation(); handleLeaveWaitlist(entry.id); }}
                                  >
                                    Sair
                                  </button>
                                </div>
                              );
                            })()}
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        {/* My Bookings */}
        {activeTab === 'mybookings' && (
          <div className="data-card">
            <div className="card-header">
              <h3 className="card-title">Minhas Reservas</h3>
            </div>
            <table className="modern-table">
              <thead>
                <tr>
                  <th>Data/Hora</th>
                  <th>Tipo</th>
                  <th>Estado</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {myBookings.length > 0 ? myBookings.map(booking => (
                  <tr key={booking.id}>
                    <td>
                      {booking.slotStartTime 
                        ? format(new Date(booking.slotStartTime), 'dd/MM/yyyy HH:mm')
                        : '-'
                      }
                    </td>
                    <td>Treino Livre</td>
                    <td>
                      <span className={`status-badge ${booking.isCancelled ? 'cancelled' : booking.isCheckedIn ? 'confirmed' : 'pending'}`}>
                        {booking.isCancelled ? 'Cancelada' : booking.isCheckedIn ? 'Check-in ✓' : 'Confirmada'}
                      </span>
                    </td>
                    <td>
                      <div className="action-buttons">
                        {!booking.isCancelled && !booking.isCheckedIn && (
                          <>
                            <button className="btn-icon" onClick={() => handleCheckIn(booking.id)} title="Check-in">✓</button>
                            <button className="btn-icon danger" onClick={() => handleCancelBooking(booking.id)} title="Cancelar">✗</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                )) : bookingsError ? (
                  <tr>
                    <td colSpan="4" className="empty-state" role="alert">
                      Não foi possível carregar as reservas — isto não quer dizer que não tenha nenhuma.
                      <button type="button" className="btn btn-outline" onClick={loadData}>Tentar de novo</button>
                    </td>
                  </tr>
                ) : (
                  <tr>
                    <td colSpan="4" className="empty-state">
                      Nenhuma reserva encontrada
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}

      </div>
    </DashboardLayout>
  );
}

export default ClientDashboard;
